// src/pages/LoginPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { guestLogin, adminLogin } from '../api/auth';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import Footer from '../components/common/Footer';
import { useLang } from '../context/LangContext';
import { fadeIn } from '../utils/animations';
import { getPublicResultsEnabled } from '../api/settings';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.background};
`;

const MainArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.lg};
`;

const CenterBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xl};
  animation: ${fadeIn} 0.5s ease-out;
`;

const Logo = styled.img`
  max-width: 280px;
  width: 100%;
  object-fit: contain;
  margin-bottom: 8px;
`;

const Slogan = styled.p`
  font-family: 'Marck Script', cursive;
  color: rgb(0, 149, 149);
  margin: 0 0 20px;
  text-align: center;
  font-size: 1.4rem;
  letter-spacing: 0.02em;
`;

const GuestLink = styled.button`
  background: none;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  padding: 16px 32px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: background 0.15s, color 0.15s;
  width: 100%;
  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: #fff;
  }
`;

/* ── Modal ── */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${({ theme }) => theme.spacing.lg};
  animation: ${fadeIn} 0.15s ease-out;
`;

const Modal = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  width: 100%;
  max-width: 360px;
`;

const ModalTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ModalField = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  label {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textLight};
    margin-bottom: ${({ theme }) => theme.spacing.xs};
  }
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.error};
  font-size: 0.85rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const ToastMsg = styled.div`
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1100;
  padding: 12px 24px;
  border-radius: 8px;
  background: #333;
  color: #fff;
  font-size: 0.9rem;
  box-shadow: 0 4px 16px rgba(0,0,0,0.25);
  animation: fadeInUp 0.3s ease-out;
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateX(-50%) translateY(12px); }
    to { opacity: 1; transform: translateX(-50%) translateY(0); }
  }
`;

/* ── Component ── */

interface LoginPageProps {
  /** Показывать ли кнопку «Вход в личный кабинет» (true — на скрытой странице). */
  showAdminLogin?: boolean;
}

const LoginPage: React.FC<LoginPageProps> = ({ showAdminLogin = false }) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useLang();

  const [showModal, setShowModal] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const passwordRef = useRef<HTMLInputElement>(null);
  const [publicResultsEnabled, setPublicResultsEnabled] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        const res = await getPublicResultsEnabled();
        setPublicResultsEnabled(res.enabled);
      } catch {
        // По умолчанию остаётся включено.
      }
    })();
  }, []);

  useEffect(() => {
    if (!toastMsg) return;
    const timer = window.setTimeout(() => setToastMsg(''), 3500);
    return () => window.clearTimeout(timer);
  }, [toastMsg]);

  useEffect(() => {
    if (showModal) {
      setPassword('');
      setError('');
      setTimeout(() => passwordRef.current?.focus(), 50);
    }
  }, [showModal]);

  const handleGuestLogin = async () => {
    try {
      const { access_token, user } = await guestLogin();
      login(access_token, user);
      navigate('/tests');
    } catch {
      setToastMsg(t('guestError'));
    }
  };

  const handleAdminSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) { setError(t('errorEmptyPassword')); return; }
    setLoading(true);
    setError('');
    try {
      const { access_token, user } = await adminLogin(username, password);
      login(access_token, user);
      navigate('/tests');
    } catch {
      setError(t('errorBadCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <MainArea>
        <CenterBox>
          <Logo src="/logo.png" alt={t('logoAlt')} />
          <Slogan>{t('homeSubtitle')}</Slogan>
          {showAdminLogin && (
            <Button variant="outline" size="lg" fullWidth onClick={() => setShowModal(true)}>
              {t('loginButton')}
            </Button>
          )}
          <GuestLink onClick={handleGuestLogin}>
            {t('guestLink')}
          </GuestLink>
          <Button variant="primary" size="lg" fullWidth disabled={!publicResultsEnabled} onClick={() => navigate('/results-gates')}>
            {t('viewResultsLink')}
          </Button>
        </CenterBox>
      </MainArea>

      {showModal && (
        <Overlay onClick={() => setShowModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{t('modalTitle')}</ModalTitle>
            <form onSubmit={handleAdminSubmit}>
              <ModalField>
                <label>{t('labelLogin')}</label>
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  fullWidth
                />
              </ModalField>
              <ModalField>
                <label>{t('labelPassword')}</label>
                <Input
                  ref={passwordRef}
                  type="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  autoComplete="current-password"
                  placeholder={t('passwordPlaceholder')}
                  fullWidth
                />
              </ModalField>
              {error && <ErrorText>{error}</ErrorText>}
              <ModalActions>
                <Button variant="primary" size="md" type="submit" disabled={loading} fullWidth>
                  {loading ? t('submitLoading') : t('submitButton')}
                </Button>
                <Button variant="outline" size="md" type="button" onClick={() => setShowModal(false)} fullWidth>
                  {t('cancelButton')}
                </Button>
              </ModalActions>
            </form>
          </Modal>
        </Overlay>
      )}

      {/* ── Toast ── */}
      {toastMsg && <ToastMsg>{toastMsg}</ToastMsg>}
      <Footer />
    </PageContainer>
  );
};

export default LoginPage;
